import type { ReactNode } from "react";
import type { HelpLink } from "../types.js";
import "./styles.css";

/**
 * client walk, 6 Oct 2026 — the ONE shape a declared `help_link` may take to become a link: an
 * absolute https URL with a host, no user info and no whitespace. ARD-DSL-080 refuses anything else
 * at deploy; this re-check is the defence in depth, so a link that slipped through renders nothing.
 */
export function safeHelpLinkUrl(url: unknown): string | null {
  if (typeof url !== "string" || url.length === 0 || /\s/.test(url)) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" || parsed.hostname.length === 0) return null;
  if (parsed.username !== "" || parsed.password !== "") return null;
  return parsed.href;
}

/**
 * The reference link under the help: plain-text words, opened in a NEW TAB (with `noopener
 * noreferrer`, so the opened page gets no handle on this form), and a spoken cue that it does.
 */
export function HelpLinkNode({ link }: { link?: HelpLink }) {
  if (!link || typeof link.label !== "string" || link.label.trim().length === 0) return null;
  const href = safeHelpLinkUrl(link.url);
  if (href === null) return null;
  return (
    <p className="field-help-link">
      <a href={href} target="_blank" rel="noopener noreferrer">
        {link.label}{" "}
        <span className="visually-hidden">(opens in a new tab)</span>
      </a>
    </p>
  );
}

export function fieldErrorId(name: string): string {
  return `${name}-err`;
}

function fieldHelpId(name: string): string {
  return `${name}-help`;
}

export function fieldHintsId(name: string): string {
  return `${name}-hints`;
}

export function describedByIds(
  name: string,
  sources: { help?: string; error?: string; hints?: ReadonlyArray<string> },
): string | undefined {
  const ids: string[] = [];
  if (sources.help) ids.push(fieldHelpId(name));
  // Alongside the help, not instead of it: the help says what to type, the hints say what the
  // system made of what was typed, and a screen reader needs both.
  if (sources.hints && sources.hints.length > 0) ids.push(fieldHintsId(name));
  if (sources.error) ids.push(fieldErrorId(name));
  return ids.length > 0 ? ids.join(" ") : undefined;
}

export interface FieldRowProps {
  name: string;
  label: string;
  required: boolean;
  /**
   * Whether the label carries the "(optional)" suffix. Defaults to `!required`. Set it `false`
   * for a row that is not a question at all (a list-only lookup's heading, card #2717): such a
   * row is neither required nor optional, so it passes `required={false}` and drops the suffix
   * here rather than claiming to be required.
   */
  optionalSuffix?: boolean;
  error?: string;
  help?: string;
  /** client walk, 6 Oct 2026 — the field's reference link, shown right after the help (ARD-DSL-080). */
  helpLink?: HelpLink;
  /**
   * Read-back lines shown UNDER the control — one per entry in the current answer, derived
   * from the value as it is typed (the `sic_codes` display preset). Plain text, always: these
   * lines carry data the declaration supplied, and the renderer never injects markup.
   */
  hints?: ReadonlyArray<string>;
  variant?: "field" | "group" | "bare";
  /** KYC-design — `half` pairs the row with the next half row at desktop widths. */
  width?: "full" | "half";
  children: ReactNode;
}

/**
 * KYC-design — a field row is label, then help, then the control, then the error. Help sits
 * ABOVE the control (the client reads what to type before typing it), and an optional field
 * says "(optional)" in its label instead of a required one carrying an asterisk. The
 * `<fieldset>` variant is for choice groups, addresses and repeating groups.
 */
export function FieldRow({ name, label, required, optionalSuffix = !required, error, help, helpLink, hints, variant = "field", width, children }: FieldRowProps) {
  const errorId = fieldErrorId(name);
  const helpId = fieldHelpId(name);
  const className = `field-row${variant === "group" ? " field-row-group" : ""}${width === "half" ? " field-row-half" : ""}`;
  const helpNode = (
    <>
      {help ? <p id={helpId} className="field-help">{help}</p> : null}
      <HelpLinkNode link={helpLink} />
    </>
  );
  const errorNode = error ? <p id={errorId} className="field-error" role="status">{error}</p> : null;
  const hintsNode = hints && hints.length > 0 ? (
    <ul id={fieldHintsId(name)} className="field-hints">
      {/* Index-keyed: the same code entered twice is a legal (if pointless) answer. */}
      {hints.map((line, i) => <li className="field-hint" key={`${i}-${line}`}>{line}</li>)}
    </ul>
  ) : null;
  const labelText = (
    <>
      {label}
      {optionalSuffix && <span className="field-optional"> (optional)</span>}
    </>
  );
  if (variant === "group") {
    return (
      <fieldset className={className} aria-describedby={describedByIds(name, { help, hints })}>
        <legend className="field-label">{labelText}</legend>
        {helpNode}
        {children}
        {hintsNode}
        {errorNode}
      </fieldset>
    );
  }
  if (variant === "bare") {
    // The control carries its own visible label (a consent card) — repeating it above
    // would read the sentence twice to a screen reader and to the eye.
    return (
      <div className={className}>
        {helpNode}
        {children}
        {hintsNode}
        {errorNode}
      </div>
    );
  }
  return (
    <div className={className}>
      <label className="field-label" htmlFor={name}>{labelText}</label>
      {helpNode}
      {children}
      {hintsNode}
      {errorNode}
    </div>
  );
}
